import type { Thought } from '../../lib/types'
import { thoughtKind, thoughtStamp } from '../../lib/text'
import { InsertMenuPanel, type MenuGroup } from './InsertMenu'

/** How many thoughts the "/" menu lists. */
export const SLASH_LIMIT = 6

interface Props {
  query: string
  /** The thoughts matching the query (the latest ones while it's empty); null while they load. */
  thoughts: Thought[] | null
  index: number
  onChoose: (thought: Thought) => void
  onHover: (index: number) => void
  /** Open the full thoughts panel on this search. */
  onBrowse: () => void
}

/**
 * The "/" menu is for your thoughts (Niki, 10/2): type "/" and some words to
 * search what you've saved, ↵ puts the thought in the column. Block types live
 * on the round "+" and their own shortcuts. It leaves focus in the text, so
 * typing keeps narrowing it.
 */
export function SlashMenu({ query, thoughts, index, onChoose, onHover, onBrowse }: Props) {
  const list = thoughts ?? []
  const groups: MenuGroup[] = list.length
    ? [
        {
          id: 'thoughts',
          title: query ? `YOUR THOUGHTS MATCHING “${query.toUpperCase()}”` : 'YOUR LATEST THOUGHTS',
          options: list.map((t) => ({
            key: `t:${t.id}`,
            label: t.body || t.title || t.source_title || t.source_url || '',
            hint: `${thoughtStamp(t.created_at)} · ${thoughtKind(t)}`,
            serif: true,
            wide: true,
          })),
        },
      ]
    : []
  const active = list[index] ? `t:${list[index].id}` : null
  const at = (key: string) => list.findIndex((t) => `t:${t.id}` === key)
  const status =
    thoughts === null
      ? 'Looking through your thoughts…'
      : list.length
        ? '↑↓ choose · ↵ insert · esc close'
        : query
          ? 'No thoughts match. Keep typing, or Esc to close.'
          : 'No thoughts yet.'
  return (
    <InsertMenuPanel
      id="wr-slash-menu"
      label="Your thoughts"
      groups={groups}
      active={active}
      onChoose={(key) => {
        const i = at(key)
        if (i >= 0) onChoose(list[i])
      }}
      onHover={(key) => {
        const i = at(key)
        if (i >= 0) onHover(i)
      }}
      footer={
        <span className="flex items-center justify-between gap-3">
          <span>{status}</span>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onBrowse}
            className="shrink-0 font-semibold text-primary hover:underline"
          >
            All thoughts
          </button>
        </span>
      }
      className="absolute left-0 top-full -mt-4"
    />
  )
}
